// Description: This is the main component of the application that renders the homepage with various widgets and functionalities.
//Homepage.jsx

import React, { useState, useEffect } from 'react';
import { toolsLabelToKey, accountLabelToKey } from '../functions/shortcutRegistry'; // DAS-2997
import { useSelector, useDispatch } from 'react-redux';
import {getDataParameters, getPortfolioData} from '../redux/reducers/portfolioReducer';
import SearchBox from '../functions/Search';
import WidgetAreaController from '../functions/Widget_area_controller';
import PortfolioView from '../components/PortfolioView';
import Account from '../components/Account';
import Alerts from '../components/Alerts';
import AboutModal from '../components/AboutModal';

import FunctionsDropdown from '../components/FunctionsDropdown';
import PortfolioAnalysisButton from '../components/PortfolioAnalysisButton';
// import StockChart from '../components/charts/Stockchart_MUI';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
import User from './Users';
import { Paper, Box } from '@mui/material';
import Button from '@mui/material/Button';
import PerformanceChart from '../components/PerformanceChart';
import WidgetGroup_RightPanel from '../components/WidgetGroup_RightPanel';
import { Stack } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import MarketWeatherBadge from '../components/MarketWeatherBadge'; // DAS-2765





const Homepage = ({ showLoginModal, setShowLoginModal, setToken }) => {

// const Homepage = () => {

    const dispatch = useDispatch();
    const userLoggedIn = useSelector((state) => state.auth.isLoggedIn);
    const demoMode = useSelector((state) => state.demoMode.isDemoMode);
    const dataParameters = useSelector((state) => state.portfolio.dataParameters);
    const portfolioTopline = useSelector((state) => state.portfolio.data?.portfolio_topline);
    const benchmarkPerformance = useSelector((state) => state.portfolio?.data?.benchmark_performance);
    const benchmarkData = useSelector((state) => state.portfolio?.parametersData?.b_dataset_json);
    const [chartData, setChartData] = useState([]);
    // DAS-2620 / DAS-2557: prefer the reactive Redux userId (set atomically with isLoggedIn
    // by the LOGIN action, and restored by redux-persist on refresh) so the getPortfolioData
    // dispatch fires reliably on first login — fixing the buttons that stayed hidden until a
    // refresh. Fall back to getUserId() (localStorage token decode) as belt-and-suspenders.
    const userId = useSelector((state) => state.auth.userId) || getUserId();
    const [shortcuts, setShortcuts] = useState([]);
  
    const isPortfolioDataAvailable =
      userLoggedIn &&
      portfolioTopline?.length > 0 &&
      benchmarkPerformance?.length > 0 &&
      benchmarkData?.length > 0;
  
    useEffect(() => {
        if (!userLoggedIn) {
          setShowLoginModal(true);
        }
      }, [userLoggedIn]);
      

    useEffect(() => {
      if (!dataParameters) {
        dispatch(getDataParameters());
      }
    }, [dataParameters, dispatch]);

    // DAS-2620: always (re)fetch portfolio data when logged in with a userId+token.
    // Re-fetching on every mount/refresh is what makes the buttons reliably appear —
    // redux-persist restores a stale portfolio.status='succeeded', so gating the fetch on
    // status would skip it and leave the buttons hidden with no refresh escape hatch.
    // The DAS-2596 reducer-level dedup guard still prevents the duplicate concurrent call.
    useEffect(() => {
      if (userLoggedIn && userId) {
        const token = localStorage.getItem('token');
        if (token) {
          dispatch(getPortfolioData(token));
        }
      }
    }, [userLoggedIn, userId, dispatch]);
  
    // Load enabled shortcut buttons
    useEffect(() => {
      if (userLoggedIn) {
        api.get('/shortcut_buttons')
          .then(res => {
            if (Array.isArray(res.data)) setShortcuts(res.data);
          })
          .catch(() => setShortcuts([]));
      } else {
        setShortcuts([]);
      }
    }, [userLoggedIn]);

    // DAS-2997: label->key maps come from the shared shortcut registry.

    const handleShortcutClick = (sc) => {
      if (!sc || !sc.name) return;
      const label = sc.name;
      if (toolsLabelToKey[label] && typeof window !== 'undefined' && window.openToolsMenu) {
        window.openToolsMenu(toolsLabelToKey[label]);
        return;
      }
      if (accountLabelToKey[label] && typeof window !== 'undefined' && window.openAccountMenu) {
        window.openAccountMenu(accountLabelToKey[label]);
        return;
      }
      console.warn('No action mapped for shortcut:', label);
    };

    const renderPortfolioButton = () => {
      if (userLoggedIn && isPortfolioDataAvailable) {
        return <PortfolioView disabled={!userLoggedIn} userId={userId} />;
      }
      return null;
    };

    useEffect(() => {
        // console.log('useeffect called');
        // console.log('userLoggedIn:', userLoggedIn);
        // console.log('userId:', userId);
        if (userLoggedIn && userId) {
          api.get('/stockchart_mui', {
            params: {
              code: 'portfolio_performance',
              userid: userId
            }
          })
          .then((res) => {
            if (res.data.status === 'success') {
                // console.log('Chart data:', res.data.data);
              setChartData(res.data.data);
            }
          })
          .catch((err) => {
            console.log(err);
            console.error('Failed to load chart data', err);
          });
        }
      }, [userLoggedIn, userId]);
      
  
    return (
      <div className="main-container">
        <div className="responsive-container">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShowChartIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            Investments Dashboard
          </h1>
          {/* DAS-2765: daily market weather flag */}
          <MarketWeatherBadge style={{ marginBottom: 8 }} />
          <div className="top-right-button">
            {!userLoggedIn && <AboutModal />}
            {demoMode && <span style={{ color: 'red', fontSize: '10px', marginLeft: '10px' }}>Demo Mode</span>}
          </div>

          <Box
            sx={{
              mt: 1,
              mb: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Stack
              direction="row"
              flexWrap="wrap"
              spacing={1.5}
              useFlexGap
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              {userLoggedIn && <SearchBox />}
              {renderPortfolioButton()}
              {userLoggedIn && isPortfolioDataAvailable && <PortfolioAnalysisButton disabled={!userLoggedIn} />}
              {userLoggedIn && <Alerts userId={userId} />}
              {userLoggedIn && <FunctionsDropdown userLoggedIn={userLoggedIn} userId={userId} />}
              <Account />
            </Stack>

            {!demoMode && shortcuts && shortcuts.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 0.5,
                }}
              >
                {shortcuts.map(sc => (
                  <Button
                    key={sc.id}
                    size="small"
                    variant="outlined"
                    onClick={() => handleShortcutClick(sc)}
                    sx={{
                      minHeight: '22px',
                      lineHeight: 1,
                      fontSize: '10px',
                      px: 0.75,
                      py: 0.25
                    }}
                  >
                    {sc.name}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
          
  
          {userLoggedIn && (
            <div className="table-grid-search-container">
              {/* <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', marginTop: '40px', marginBottom: '40px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}> */}
              <Accordion defaultExpanded disableGutters sx={{ marginTop: '20px' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Portfolio Performance & Market Data</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    display="flex"
                    gap="12px"
                    mt={0}
                    mb={1}
                    justifyContent="space-between"
                    alignItems="stretch"
                    sx={{ height: '500px' }} // fixed height for most sections
                    // sx={{ height: '100%', minHeight: '300px' }} // <— enforce height
                  >
                    <Box
                      sx={{
                        flex: '1 1 60%',
                        minWidth: '300px',
                        borderRadius: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Paper
                        elevation={4}
                        sx={{
                          flexGrow: 1,
                          height: '100%',
                          borderRadius: '16px',
                          padding: '20px',
                          display: 'flex',
                          flexDirection: 'column',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                        }}
                      >
                        <PerformanceChart />
                      </Paper>
                    </Box>

                    <Box
                      sx={{
                        minWidth: '400px',             // ✅ minimum to hold 4x 100px + padding
                        maxWidth: '800px',             // ✅ allow expansion for up to 8 columns
                        borderRadius: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        flexShrink: 0,                 // 🔒 don't compress
                      }}
                    >
                      <Paper
                        elevation={2}
                        sx={{
                          flexGrow: 1,
                          height: '100%',
                          borderRadius: '16px',
                          padding: '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          overflowY: 'auto',
                          overflowX: 'hidden', // DAS-2566: overflowY:auto computes overflow-x to auto; pin it hidden so no horizontal bar
                        }}
                      >
                        <WidgetGroup_RightPanel />
                      </Paper>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded disableGutters sx={{ marginTop: '40px' }}>

                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Investment Management Widgets</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <WidgetAreaController />
                </AccordionDetails>
              </Accordion>
            </div>
          )}
        </div>

        <User
  defaultTab="2"
  visible={showLoginModal}
  setVisible={setShowLoginModal}
  setToken={setToken}
/>


      </div>
    );
  };
  
  export default Homepage;
  